import React, {
    ChangeEvent,
    Key,
    MouseEventHandler,
    ReactElement,
    ReactNode,
    useCallback,
    useContext,
    useMemo
} from 'react'
import clsx from 'clsx'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TablePagination, { LabelDisplayedRowsArgs } from '@material-ui/core/TablePagination'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableRow from '@material-ui/core/TableRow'
import Checkbox from '@material-ui/core/Checkbox'
import { OnSortColumnClickFn, TableHead } from './TableHead'

import { defaultSortingSequence, SortDirection, SorterFn, useSort, useSortReturn } from '../../../Hooks/useSort'
import { useSelection, useSelectionReturn } from '../../../Hooks/useSelection'
import { DeleteAddToolbar, DeleteAddToolbarLocaleText, DeleteAddToolbarLocaleTextDefaults } from './DeleteAddToolbar'
import { DataTableRowLoader } from './DataTableRowLoader'
import { AdminBackendPermissionNames } from '../../../../React/Common/Constants/Permissions'
import { usePermissions, UserPermissions } from '../../../Hooks/usePermissions'
import { isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import { usePagination, usePaginationReturn } from '../../../Hooks/usePagination'
import { LanguageContext, PromptContext } from '../../../../React/Common/Contexts'
import { PromptDisplayType } from '../../../../React/Common/Contexts/PromptContext'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageDataTable } from '../../../../../i18n'
import { COMMA, SPACE } from '../../../../NonReact/constants/Separators'

interface DataTableLocaleText extends DeleteAddToolbarLocaleText {
    noRows: string
}

const DataTableLocaleTextDefaults: DataTableLocaleText = {
    ...DeleteAddToolbarLocaleTextDefaults,
    noRows: 'Nothing to show here'
}

export enum TABLE_SIZE {
    MEDIUM = 'medium',
    SMALL = 'small'
}

export interface DataTableColumn<T, K extends keyof T> {
    /**
     * The object property that will be rendered in each cell.
     *
     * For example if we have data like this [{name: "yanis"}]
     * and we want to show the name
     * we need to set the field to "name" in the columns [{field:"name"}].
     *
     * "renderCell" will take precedence over "field".
     */
    field: K
    /**
     * React key, has to be unique.
     */
    key: string
    /**
     * Name that is going to be displayed on the head column.
     */
    headerName?: string
    /**
     * Function used to render the data in the table cell.
     */
    renderCell?: (row: T, rowIndex: number, column: DataTableColumn<T, K>, key: string | number) => ReactNode
    /**
     * Function used to sort data from this column.
     */
    sortFunction?: SorterFn<T>
    /**
     * Disables sorting for the column.
     */
    sortable?: boolean
    /**
     * Stops cell click event propagation.
     * Useful when "renderCell" renders some interactable element and we don't want it to trigger a row selection.
     */
    disableRowSelection?: boolean
}

type DataTablePaginationProps = {
    /**
     * Disables pagination controls.
     */
    paginationDisabled?: boolean
    /**
     * If set to true it won't do pagination work, the pagination needs to be handled outside using the pagination events.
     * When set to false the data is divided into pages internally.
     * This is useful when we fetch too much data upfront and don't want to display it all in one page.
     */
    paginationServer?: boolean
    /**
     * Options that show on the "Rows per page"
     *
     * @default [25, 50, 100]
     */
    defaultRowsPerPageOptions?: Array<number | { value: number; label: string }>
    /**
     * Default rows per page.
     * If defaultRowsPerPageOptions is set it should match one of its values
     */
    defaultRowsPerPage?: number
    /**
     * Default total rows count.
     * Sometimes it can be expensive to know the total, in that case set it to -1.
     * When working with token/time server based pagination set it to -1.
     */
    defaultRowsCount?: number
    /**
     * Default page number.
     * Use it to control page number from outside
     */
    defaultPageNumber?: number
    /**
     * Callback fired when the number current page is changed.
     */
    onPageChange?: (newPage: number, rowsPerPage: number) => void
    /**
     * Callback fired when the number of rows per page is changed.
     */
    onRowsPerPageChange?: (currentPage: number, newRowsPerPage: number) => void
}

type DataTableSelectionProps = {
    isSelectable?: boolean
}

type DataTableSortProps<T, K extends keyof T> = {
    /**
     * If set to true it won't do sorting work, sorting needs to be handled outside using the sorting events.
     * If set to false sorting will be done internally.
     */
    sortServer?: boolean
    /**
     * Callback fired when a sorting event is triggered.
     */
    onSortChange?: (column: DataTableColumn<T, K>, sortingOrder: SortDirection) => void
}

export type DataTableProps<T extends { key: Key; [key: string]: Key }, K extends keyof T> = {
    columns: DataTableColumn<T, K>[]
    permissionName?: AdminBackendPermissionNames
    data: T[]
    totalCount?: number
    colIndexToTriggerClick?: number
    /**
     * Translations
     */
    localeText?: Partial<DataTableLocaleText>
    /**
     * Table is in loading state.
     */
    isLoading?: boolean
    /**
     * Removes most of the padding from the table
     */
    dense?: boolean
    /**
     * Display delete-add toolbar.
     */
    toolbarDisabled?: boolean
    /**
     * Callback fired when a toolbar add event is triggered.
     */
    onToolbarAddClick?: () => void
    /**
     * Callback fired when a toolbar change event is triggered.
     */
    onToolbarChangeClick?: (selectedItems: T[]) => void
    /**
     * Callback fired when a toolbar view event is triggered.
     */
    onToolbarViewClick?: (selectedItems: T[]) => void
    /**
     * Callback fired when a toolbar delete event is triggered.
     */
    onToolbarDeleteClick?: (selectedItems: T[]) => void
} & DataTablePaginationProps &
    DataTableSelectionProps &
    DataTableSortProps<T, K>

export type DataTableSortDirection = SortDirection

export function DataTable<T extends { key: Key; Description: string; [key: string]: Key }, K extends keyof T>({
    columns,
    permissionName,
    colIndexToTriggerClick,
    data,
    localeText,
    isLoading,
    dense,
    toolbarDisabled,
    paginationDisabled,
    paginationServer,
    defaultRowsPerPageOptions,
    defaultRowsPerPage,
    defaultRowsCount,
    defaultPageNumber,
    onPageChange,
    onRowsPerPageChange,
    isSelectable,
    sortServer,
    onSortChange,
    onToolbarDeleteClick,
    onToolbarAddClick,
    onToolbarChangeClick,
    onToolbarViewClick,
    totalCount
}: DataTableProps<T, K>): ReactElement {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(LanguageDataTable.NAME)

    const { displayPromptPromise }: PromptDisplayType = useContext(PromptContext)

    const lang: DataTableLocaleText = useMemo(
        (): DataTableLocaleText => ({ ...DataTableLocaleTextDefaults, ...localeText }),
        [localeText]
    )

    const userPermissions: UserPermissions = usePermissions(permissionName)

    const [sortedData, sort, sortDirection, sortOrderBy, nextSortDirection]: useSortReturn<T, K> = useSort(
        data,
        !!sortServer
    )

    const [pageNumber, getPage, rowsPerPage, changeRowsPerPage]: usePaginationReturn<T> = usePagination(
        sortedData,
        !paginationDisabled,
        defaultPageNumber ?? 0,
        defaultRowsPerPage || 25,
        !paginationServer
    )

    const [
        selectionCount,
        selectionState,
        isSelected,
        selectionToggleOne,
        selectionToggleMulti,
        selectionToggleAll,
        reset
    ]: useSelectionReturn<T> = useSelection(getPage(pageNumber), isSelectable || false)

    const handleColumnSortClick: OnSortColumnClickFn<T, K> = useCallback(
        (column: DataTableColumn<T, K>): void => {
            const nextDirection: DataTableSortDirection =
                column.field === sortOrderBy ? nextSortDirection() : defaultSortingSequence[0]

            sort(column.sortFunction, column.field, nextDirection)
            if (onSortChange) {
                onSortChange(column, nextDirection)
            }
        },
        [nextSortDirection, onSortChange, sort, sortOrderBy]
    )

    const handleSelectAllClick: () => void = useCallback((): void => {
        if (!isSelectable) {
            return
        }

        selectionToggleAll(getPage(pageNumber))
    }, [isSelectable, getPage, pageNumber, selectionToggleAll])

    const handleRowClick: (name: Key) => void = useCallback(
        (name: Key): void => {
            if (!isSelectable) {
                return
            }

            selectionToggleOne(name, getPage(pageNumber))
        },
        [isSelectable, selectionToggleOne, getPage, pageNumber]
    )

    const handleShiftRowClick: (key: Key) => void = useCallback(
        (key: Key): void => {
            if (!isSelectable) {
                return
            }

            const currentItems: T[] = getPage(pageNumber)

            // Find previous selected item
            const indexOfClicked: number = currentItems.findIndex((currentItem: T): boolean => currentItem.key === key)

            if (indexOfClicked === -1) {
                selectionToggleOne(key, currentItems)

                return
            }

            // Get items up to the clicked item
            const itemsUptoClicked: T[] = currentItems.slice(0, indexOfClicked + 1)

            // Find the last selected item before the clicked one
            const lastSelectedIndex: number = itemsUptoClicked.reduce(
                (lastIndex: number, item: T, index: number): number => (isSelected(item.key) ? index : lastIndex),
                -1
            )

            if (lastSelectedIndex === -1) {
                selectionToggleOne(key, currentItems)

                return
            }

            // Get items between last selected and clicked item
            const itemsToSelect: T[] = itemsUptoClicked.slice(lastSelectedIndex + 1)
            const keysOfItemsToSelect: Key[] = itemsToSelect.map((item: T): Key => item.key)

            selectionToggleMulti(keysOfItemsToSelect, currentItems)
        },
        [isSelectable, selectionToggleOne, selectionToggleMulti, getPage, pageNumber]
    )

    const handleToolbarAddClick: MouseEventHandler<HTMLButtonElement> = useCallback((): void => {
        if (onToolbarAddClick) {
            return onToolbarAddClick()
        }

        return undefined
    }, [onToolbarAddClick])

    const handleClickWithSelectedOptions = useCallback(
        (func?: (selectedItems: T[]) => void): void => {
            func?.(
                data.reduce((acc: T[], item: T): T[] => {
                    if (isSelected(item.key)) {
                        acc.push(item)
                    }

                    return acc
                }, [])
            )
        },
        [data, isSelected]
    )

    const handleToolbarViewClick: MouseEventHandler<HTMLButtonElement> = useCallback(
        (): void => handleClickWithSelectedOptions(onToolbarViewClick),
        [onToolbarViewClick, data, isSelected]
    )

    const handleToolbarChangeClick: MouseEventHandler<HTMLButtonElement> = useCallback(
        (): void => handleClickWithSelectedOptions(onToolbarChangeClick),
        [onToolbarChangeClick, data, isSelected]
    )

    const handleToolbarDeleteClick: MouseEventHandler<HTMLButtonElement> = useCallback((): void => {
        displayPromptPromise(
            language.getInterpolated(
                LanguageDataTable.DataTable_AreYouSure,
                data
                    .filter((d: T): boolean => isSelected(d.key))
                    .map((d: T): string => d.Description)
                    .join(`${COMMA}${SPACE}`)
            )
        )
            .then((): void => handleClickWithSelectedOptions(onToolbarDeleteClick))
            .catch((): void => {
                // Do nothing, user cancelled the prompt
            })
    }, [onToolbarDeleteClick, data, isSelected])

    const handleRowKeyClick = useCallback(
        (row: T): void => {
            reset()

            if (userPermissions?.Read && !userPermissions?.Update) {
                onToolbarViewClick?.([row])
            } else {
                onToolbarChangeClick?.([row])
            }
        },
        [onToolbarChangeClick, onToolbarViewClick, userPermissions]
    )

    const handlePageChange: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void = useCallback(
        (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number): void => {
            if (onPageChange) {
                onPageChange(newPage, rowsPerPage)
            }

            getPage(newPage)
        },
        [getPage, onPageChange, rowsPerPage, paginationServer]
    )

    const handleChangeRowsPerPage: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            const newValue: number = Number(event.target.value)

            if (onRowsPerPageChange) {
                onRowsPerPageChange(pageNumber, newValue)
            }

            changeRowsPerPage(newValue)

            if (!paginationServer) {
                getPage(0)
            }
        },
        [changeRowsPerPage, getPage, onRowsPerPageChange, pageNumber, paginationServer]
    )

    return (
        <div className={clsx('router-datatable')}>
            <TableContainer>
                <Table
                    className='rd-table'
                    aria-labelledby='tableTitle'
                    size={dense ? TABLE_SIZE.SMALL : TABLE_SIZE.MEDIUM}
                    aria-label='data table'
                >
                    <TableHead
                        isSelectable={isSelectable || false}
                        sortDirection={sortDirection}
                        sortOrderBy={sortOrderBy}
                        onSelectAllClick={handleSelectAllClick}
                        onSortColumnClick={handleColumnSortClick}
                        columns={columns}
                        selectionState={selectionState}
                    />
                    <TableBody className='router-datatable-body'>
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {isLoading ? (
                            <DataTableRowLoader noRows={rowsPerPage} noColumns={5} />
                        ) : getPage(pageNumber).length > 0 ? (
                            getPage(pageNumber).map((row: T, rowIdx: number): ReactNode => {
                                const isItemSelected: boolean = isSelected(row.key)
                                const labelId: string = `enhanced-table-checkbox-${rowIdx}`

                                return (
                                    <TableRow
                                        className='rd-table-row'
                                        hover
                                        onClick={(event: React.MouseEvent): void => {
                                            if (event.shiftKey) {
                                                handleShiftRowClick(row.key)
                                            } else {
                                                handleRowClick(row.key)
                                            }
                                        }}
                                        role='checkbox'
                                        aria-checked={isItemSelected}
                                        key={row.key}
                                        selected={isItemSelected}
                                    >
                                        {isSelectable ? (
                                            <TableCell
                                                padding='checkbox'
                                                key={`${row.key}:selectable`}
                                                className='router-datatable-body-selection-cell'
                                            >
                                                <Checkbox
                                                    checked={isItemSelected}
                                                    inputProps={{ 'aria-labelledby': labelId }}
                                                />
                                            </TableCell>
                                        ) : (
                                            <></>
                                        )}
                                        {columns.map(
                                            (col: DataTableColumn<T, K>, idx: number): ReactNode => (
                                                <TableCell
                                                    key={`${row.key}:${col.key}`}
                                                    className={
                                                        isVarNotEmpty(colIndexToTriggerClick) &&
                                                        idx === colIndexToTriggerClick
                                                            ? 'router-datatable-body-clickable'
                                                            : EMPTY_STRING
                                                    }
                                                    id={idx === 0 ? labelId : undefined}
                                                    align='left'
                                                    onMouseDown={(): void =>
                                                        isVarNotEmpty(colIndexToTriggerClick) &&
                                                        idx === colIndexToTriggerClick
                                                            ? handleRowKeyClick(row)
                                                            : undefined
                                                    }
                                                    onClick={
                                                        col.disableRowSelection
                                                            ? (e: React.MouseEvent): void => e.stopPropagation()
                                                            : undefined
                                                    }
                                                >
                                                    {col.renderCell
                                                        ? col.renderCell(row, rowIdx, col, row.key)
                                                        : row[col.field]}
                                                </TableCell>
                                            )
                                        )}
                                    </TableRow>
                                )
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5}>{localeText?.noRows}</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <div className='router-datatable-toolbar-container'>
                {!toolbarDisabled ? (
                    <DeleteAddToolbar
                        itemsSelectedCount={selectionCount}
                        onAddClick={handleToolbarAddClick}
                        onChangeClick={handleToolbarChangeClick}
                        onDeleteClick={handleToolbarDeleteClick}
                        onViewClick={handleToolbarViewClick}
                        localeText={lang}
                        permissionName={permissionName}
                    />
                ) : (
                    <></>
                )}
                {!paginationDisabled ? (
                    <TablePagination
                        rowsPerPageOptions={defaultRowsPerPageOptions || [25, 50, 100]}
                        component='div'
                        count={paginationServer ? totalCount ?? -1 : defaultRowsCount || data.length}
                        rowsPerPage={rowsPerPage}
                        page={pageNumber}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage={language.get(LanguageDataTable.DataTable_RowsPerPage)}
                        labelDisplayedRows={({ from, to, count }: LabelDisplayedRowsArgs): string =>
                            language.getInterpolated(LanguageDataTable.DataTable_Of, from, to, count)
                        }
                        nextIconButtonText={language.get(LanguageDataTable.DataTable_Next)}
                        backIconButtonText={language.get(LanguageDataTable.DataTable_Previous)}
                    />
                ) : (
                    <></>
                )}
            </div>
        </div>
    )
}
